import {useMemo, useState} from "react";
import {InsightPieChart, InsightPieChartProps} from "../InsightPieChart/InsightPieChart";
import {PieChartData} from "../../chart/PieChart/PieChart";
import {InsightEmpty} from "../InsightEmpty/InsightEmpty";
import {ActionIcon, Center, Flex, Stack} from "@mantine/core";
import {IconSearch} from "@tabler/icons-react";
import {useNavigate} from "react-router-dom";
import {BaseSelect} from "../../select/base-select";
import {useTranslation} from "react-i18next";

export type AvailableLoadsBy = {
  company: Record<string, number>
  route: Record<string, number>
}

export type AvailableLoadsByInsightProps =
  Pick<InsightPieChartProps, 'tooltipPos' | 'containerProps' | 'loading' | 'id' | 'mih'>
  & {
  data?: AvailableLoadsBy
  title?: string
  showEmpty?: boolean
  showSearch?: boolean
  showSelect?: boolean
  defaultType?: "company" | "route"
}

export const AvailableLoadsByInsight = (props: AvailableLoadsByInsightProps) => {
  const {
    title,
    data,
    showEmpty = true,
    showSearch = false,
    showSelect = false,
    defaultType = "company",
    ...rest
  } = props

  const {t} = useTranslation(["common"])
  const navigate = useNavigate()

  const [type, setType] = useState<string | null>(defaultType)

  const chartData: PieChartData[] | undefined = useMemo(() => {
    const chartData: PieChartData[] = []

    if (data) {
      let records: Record<string, number> = data.company

      if (type === "route") {
        records = data.route
      }

      Object.keys(records).forEach((k) => {
        chartData.push({
          name: k,
          value: records[k]
        })
      })
    }

    return chartData
  }, [data, type])

  if (showEmpty && (!chartData || chartData.length === 0)) {
    return (
      <InsightEmpty {...rest} title={title ?? t<string>("common.insight.availableloadsby", "Avail. Loads By")}>
        {showSearch &&
          <Flex justify="flex-end">
            <ActionIcon onClick={() => navigate("/insights/available-loads-by")}>
              <IconSearch size="1.125rem"/>
            </ActionIcon>
          </Flex>
        }
      </InsightEmpty>
    )
  }

  return (
    <InsightPieChart
      {...rest}
      title={title ?? t("common.insight.availableloadsby", "Avail. Loads By")}
      data={chartData}>
      {(showSearch || showSelect) &&
        <Stack>
          {(showSelect) &&
            <Center>
              <BaseSelect
                placeholder={"By"}
                data={[
                  {value: "company", label: "Company"},
                  {value: "route", label: "Route"},
                ]}
                defaultValue={type}
                onChange={setType}/>
            </Center>
          }
          {showSearch &&
            <Flex justify="flex-end">
              <ActionIcon onClick={() => navigate("/insights/available-loads-by")}>
                <IconSearch size="1.125rem"/>
              </ActionIcon>
            </Flex>
          }
        </Stack>
      }
    </InsightPieChart>
  )
}
